import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "./supabase";

function getAuthCallbackUrl() {
  return `${window.location.origin}/auth/callback`;
}

/** Supabase OAuth provider slugs (LinkedIn uses the OIDC variant). */
export type OAuthProvider = "google" | "github" | "linkedin_oidc";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithOAuth: (provider: OAuthProvider, returnTo?: string) => Promise<void>;
  linkIdentity: (provider: OAuthProvider) => Promise<void>;
  unlinkIdentity: (identityId: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Compare two sessions by user identity to avoid unnecessary re-renders.
 * Supabase fires onAuthStateChange on every tab focus (visibilitychange)
 * AND on every token refresh (~hourly). Both create a new session object.
 * Without this guard, each event triggers a full React tree re-render,
 * which cascades through ProtectedRoute → useBetaAgreement → loading flash
 * → unmount/remount of the entire page tree, destroying component state
 * (e.g. mining chat view, drafts, reducer state).
 *
 * We compare only user.id because:
 * - Tab focus: same user, same token → skip (already handled)
 * - Token refresh: same user, NEW token → skip React state update
 *   (the Supabase client internally uses the refreshed token for API
 *   calls; React state only needs to know WHO is logged in)
 * - Actual sign-in/sign-out: different user or null → update
 *
 * Code that needs a fresh access_token (e.g. Edge Function calls)
 * already calls supabase.auth.getSession() directly.
 */
function sessionsMatch(a: Session | null, b: Session | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.user?.id === b.user?.id;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionRef = useRef<Session | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: initial } }) => {
      sessionRef.current = initial;
      setSession(initial);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Nuclear defense-in-depth for cross-user cache leakage (sw-5mg): even
      // with user-scoped React Query keys, drop all cached data on sign-out
      // so no stale query, mutation, or mutation-result from the previous
      // user can be served to the next. Fires before the session null-out so
      // components unmounting on sign-out don't refetch with the old user's
      // cache still warm.
      if (event === "SIGNED_OUT") {
        queryClient.clear();
      }

      // Supabase fires SIGNED_IN on every tab focus (visibilitychange →
      // _recoverAndRefresh) with a new session object reference. Skip the
      // state update when the session is functionally identical to avoid
      // unnecessary re-renders that unmount components and lose local state.
      if (sessionsMatch(sessionRef.current, newSession)) {
        return;
      }
      sessionRef.current = newSession;
      setSession(newSession);
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  }, []);

  const signInWithOAuth = useCallback(async (provider: OAuthProvider, returnTo?: string) => {
    // Thread the post-login destination through the provider round-trip so
    // flows like /oauth/consent (MCP connector authorization) resume after
    // social sign-in instead of being dropped at "/".
    const callback = returnTo
      ? `${getAuthCallbackUrl()}?returnTo=${encodeURIComponent(returnTo)}`
      : getAuthCallbackUrl();
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: callback,
      },
    });
    if (error) throw error;
  }, []);

  const linkIdentity = useCallback(async (provider: OAuthProvider) => {
    const { error } = await supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo: getAuthCallbackUrl(),
      },
    });
    if (error) throw error;
  }, []);

  const unlinkIdentity = useCallback(
    async (identityId: string) => {
      const identity = session?.user?.identities?.find((i) => i.id === identityId);
      if (!identity) throw new Error("Identity not found");
      const { error } = await supabase.auth.unlinkIdentity(identity);
      if (error) throw error;
    },
    [session],
  );

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getAuthCallbackUrl(),
    });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }, []);

  const resendVerificationEmail = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resend({ type: "signup", email });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signInWithEmail,
      signUp,
      signInWithOAuth,
      linkIdentity,
      unlinkIdentity,
      resetPassword,
      updatePassword,
      resendVerificationEmail,
      signOut,
    }),
    [
      session,
      loading,
      signInWithEmail,
      signUp,
      signInWithOAuth,
      linkIdentity,
      unlinkIdentity,
      resetPassword,
      updatePassword,
      resendVerificationEmail,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
